/**
 * The certification boards' records, carried to the library (build 2).
 *
 *   cert.add    a new certificate: the document, its first issue, in force
 *   cert.renew  a renewal: a new issue of the same certificate
 *   cert.issue  putting a recorded issue in force (an approver)
 *   cert.file   the paper for an issue recorded without it
 *   cert.work   RAISE WORK TO CLEAR IT: a work order in the register
 *
 * A certificate goes in force at once only for someone who holds
 * document:approve; anyone else's is recorded and waits, and the notice says
 * so in plain words. Who recorded it and when are the platform's stamps — no
 * name or clock from this browser is sent.
 *
 * Nothing here imports the demo domain: this sits in the entry chunk.
 */
import { hasPermission, PERMISSION, type Principal } from "../../../api/dataSource";
import {
  CERTIFICATE_TYPE,
  type CertificateIssueInput,
  type CertificateWrite,
  type NewCertificateInput,
} from "../../../domain/certificates";
import type { CreateWorkOrderInput } from "../../../domain/workOrders";
import { t } from "../../../i18n";
import { registerWrite, type WriteAck } from "../writes";

/** A certificate as the boards hold it (demo/domain/tables.ts CERTS, or one fed from the library). */
export interface CertRecord {
  id: string;
  /** the library document's id, on a certificate fed from the platform */
  platformId?: string;
  asset?: string;
  assetName?: string;
  kind?: string;
  title?: string;
  body?: string;
  ref?: string;
  issued?: string;
  expires?: string;
  owner?: string;
  needs?: string[];
  domain?: string;
  evidence?: string[];
  note?: string;
  /** a recorded issue waiting to be put in force */
  pending?: { id: string; revision?: string };
}

/** What the add form hands over beside the record. */
export interface AddExtra {
  /** the reference as typed — blank when the form had none */
  reference?: string;
  file?: File;
}

/** What the renewal form hands over beside the record. */
export interface RenewExtra {
  /** the new reference as typed — blank when none */
  revision?: string;
  issuedOn?: string;
  expiresOn?: string;
  issuedBy?: string;
  note?: string;
}

const day = (s?: string): string | undefined => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : undefined);
const text = (s?: string): string | undefined => {
  const v = s?.trim();
  return v && v !== "—" ? v : undefined;
};

/** The label of an issue: the reference printed on it, or — with none — its expiry. */
export function issueLabel(reference: string | undefined, expiresOn: string): string {
  return text(reference) ?? `Issue to ${expiresOn}`;
}

const canIssue = (p: Principal): boolean => hasPermission(p, PERMISSION.documentsApprove);

/** cert.add → the library's three requests (domain/certificates.ts). Pure, for the tests. */
export function newCertificate(
  rec: CertRecord,
  extra: AddExtra | null,
  siteId: string,
  issueNow: boolean,
): NewCertificateInput {
  const expiresOn = day(rec.expires) ?? "";
  const assetName = text(rec.assetName) ?? "";
  const title = text(rec.title) ?? "";
  const reference = text(extra?.reference);
  return {
    siteId,
    type: CERTIFICATE_TYPE,
    /* the library, the Regulatory desk and Sky show only the title, so it names the asset too */
    title: assetName && title ? `${assetName} — ${title}` : title || assetName,
    reference,
    tags: ["certificate", ...(rec.kind ? [rec.kind] : [])],
    attrs: {
      register: "certification",
      kind: rec.kind ?? "statutory",
      ...(text(rec.asset) ? { asset_tag: text(rec.asset) } : {}),
      asset_name: assetName,
      ...(title ? { certificate: title } : {}),
      ...(text(rec.owner) ? { owner: text(rec.owner) } : {}),
      ...(text(rec.body) ? { body: text(rec.body) } : {}),
      ...(rec.domain ? { domain: rec.domain } : {}),
      needs: rec.needs ?? [],
      evidence: rec.evidence ?? [],
    } as NewCertificateInput["attrs"],
    issue: {
      revision: issueLabel(reference, expiresOn),
      issuedOn: day(rec.issued),
      expiresOn,
      changeNote: text(rec.note) ?? "Recorded from the certification register",
      ...(extra?.file ? { file: extra.file } : {}),
      issueNow,
    },
  };
}

/** cert.renew → one new issue of the certificate. Pure, for the tests. */
export function renewal(extra: RenewExtra | null, issueNow: boolean): CertificateIssueInput {
  const expiresOn = day(extra?.expiresOn) ?? "";
  return {
    revision: issueLabel(extra?.revision, expiresOn),
    issuedOn: day(extra?.issuedOn),
    expiresOn,
    ...(text(extra?.note) ? { changeNote: text(extra?.note) } : {}),
    /* who issued THIS one; the body the certificate names stays on the document */
    ...(text(extra?.issuedBy) ? { issuedBy: text(extra?.issuedBy) } : {}),
    issueNow,
  };
}

/** What to say once the platform has it. */
export function said(what: "certificate" | "renewal", w: CertificateWrite): Pick<WriteAck, "text" | "tone"> {
  if (w.issued)
    return {
      tone: "ok",
      text:
        what === "certificate"
          ? t("Certificate {ref} saved on the platform and in force.", { ref: w.revision })
          : t("Renewal {ref} saved on the platform and in force.", { ref: w.revision }),
    };
  if (w.waiting === "review")
    return {
      tone: "warn",
      text: t("{ref} saved on the platform. This kind of certificate is reviewed before it goes in force.", {
        ref: w.revision,
      }),
    };
  return {
    tone: "warn",
    text: t("{ref} saved on the platform. It goes in force when someone who can approve documents issues it.", {
      ref: w.revision,
    }),
  };
}

const documents = (): unknown[][] => [["documents"]];

registerWrite("cert.add", {
  permission: () => PERMISSION.documentsWrite,
  invalidate: documents,
  /* each of the three requests carries a key derived from the record's, so TRY AGAIN cannot file twice */
  retryable: true,
  async send(ctx, rec: CertRecord, extra: AddExtra | null) {
    const w = await ctx.source.recordCertificate(
      newCertificate(rec, extra, ctx.siteId, canIssue(ctx.principal)),
      ctx.key,
    );
    return { id: w.documentId, reference: w.revision, kind: "document", ...said("certificate", w) };
  },
});

registerWrite("cert.renew", {
  permission: () => PERMISSION.documentsWrite,
  invalidate: documents,
  retryable: true,
  async send(ctx, rec: CertRecord, extra: RenewExtra | null) {
    const w = await ctx.source.renewCertificate(
      rec.platformId ?? rec.id,
      renewal(extra, canIssue(ctx.principal)),
      ctx.key,
    );
    /* the revision's id: a refill keeps the board's copy until the library lists this issue */
    return { id: w.revisionId, reference: w.revision, kind: "revision", ...said("renewal", w) };
  },
});

registerWrite("cert.issue", {
  permission: () => PERMISSION.documentsApprove,
  invalidate: documents,
  /* an issue already in force is refused, never put in force twice */
  retryable: true,
  async send(ctx, rec: CertRecord, extra: { revisionId?: string } | null) {
    const revisionId = extra?.revisionId ?? rec.pending?.id;
    if (!revisionId) throw new Error(t("There is no recorded issue waiting to go in force."));
    await ctx.source.issueCertificate(revisionId);
    const ref = rec.pending?.revision ?? rec.ref ?? revisionId;
    return {
      id: revisionId,
      reference: ref,
      kind: "revision",
      text: t("Certificate issue {ref} is now in force.", { ref }),
    };
  },
});

registerWrite("cert.file", {
  permission: () => PERMISSION.documentsWrite,
  invalidate: documents,
  /* the platform takes one file per issue; a second attempt is refused, not stored */
  retryable: true,
  async send(ctx, rec: CertRecord, extra: { revisionId?: string; file?: File } | null) {
    if (!extra?.revisionId || !extra.file) throw new Error(t("Choose the issue and the file to attach."));
    const a = await ctx.source.attachRevisionFile(extra.revisionId, extra.file, ctx.key);
    return {
      id: a.revisionId,
      reference: extra.file.name,
      kind: "revision",
      text: t("{name} filed with {ref} on the platform.", { name: extra.file.name, ref: rec.ref ?? rec.title ?? "" }),
    };
  },
});

/** What RAISE WORK TO CLEAR IT hands over. */
export interface WorkExtra {
  title: string;
  priority: 1 | 2;
  description?: string;
  assetTag?: string;
}

export function certificateWork(extra: WorkExtra, siteId: string): CreateWorkOrderInput {
  return {
    siteId,
    title: extra.title,
    /* an expired certificate is a fault to clear; one coming due is planned work */
    kind: extra.priority === 1 ? "corrective" : "preventive",
    priority: extra.priority,
    ...(extra.description ? { description: extra.description } : {}),
    /* a tag the platform does not hold is ignored there, not refused */
    ...(text(extra.assetTag) ? { assetTag: text(extra.assetTag) } : {}),
  };
}

registerWrite("cert.work", {
  permission: () => PERMISSION.workOrdersWrite,
  invalidate: (siteId) => [["work-orders"], ["kpis", siteId]],
  /* the work order endpoint mints its own client id per call: a retry could raise two */
  retryable: false,
  async send(ctx, _rec, extra: WorkExtra) {
    const wo = await ctx.source.createWorkOrder(certificateWork(extra, ctx.siteId));
    return {
      id: wo.id,
      reference: wo.number,
      kind: "work_order",
      text: t("{ref} raised in the work order register for this certificate.", { ref: wo.number }),
    };
  },
});
